import { createGlobalStyle, css } from 'styled-components'
import { getTextAlign } from '@applyft-web/ui-components'

export const GlobalStyles = createGlobalStyle(
  ({ theme }) => css`
    :root {
      --system-ui:
        'Inter Variable', Arial, system-ui, 'Segoe UI', Roboto, Helvetica, sans-serif,
        'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      font-family: var(--system-ui);
      line-height: 130%;
      -webkit-tap-highlight-color: transparent;
      text-size-adjust: none;

      &::-webkit-scrollbar {
        width: 0;
        height: 0;
      }

      &::before,
      &::after {
        box-sizing: border-box;
      }
    }

    /* html, body and #root grow with the content instead of being pinned to the
       viewport height, so the gradient on html stretches over the whole page and
       scrolls with it. #root is a grid so that pages with height: 100% still fill
       at least the viewport. */
    html {
      height: auto;
      min-height: 100%;
      overscroll-behavior: none;
      background: ${theme.colors?.bodyBackground};
    }

    body {
      width: 100svw;
      height: auto;
      min-height: calc(var(--vh, 1svh) * 100);
      color: ${theme.colors?.text};
      text-align: ${getTextAlign(theme.isRtl)};
      user-select: none;
      position: relative;
      overflow-x: hidden;
      background: transparent;
      -webkit-font-smoothing: antialiased;

      & > * {
        width: auto;
      }

      /* ui-components gives every body child height: 100%. Iframes that third
         parties append to body (e.g. Braintree's hidden dispatch frame, absolute
         with height="0") would then be as tall as the whole page and double the
         scroll height, so they get their own width/height attributes back. */
      & > iframe {
        width: revert-layer;
        height: revert-layer;
      }
    }

    #root {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      min-height: calc(var(--vh, 1svh) * 100);
    }

    input[type='text'],
    input[type='tel'],
    input[type='email'],
    input[type='password'],
    input[type='textarea'],
    input[type='radio'],
    textarea,
    input {
      appearance: none;
    }

    a,
    input,
    button {
      font-family: var(--system-ui);
    }

    /* RTL locales (ar, he) mirror the whole document via <html dir>, but values that
       are not natural language — emails, phone numbers, card data — are always read
       left-to-right. Mirroring them breaks the leading "+", digit grouping and the
       local@domain order, so they opt out of the flip. */
    input[type='email'],
    input[type='tel'],
    input[type='number'],
    input[type='url'],
    input[type='password'],
    input[inputmode='email'],
    input[inputmode='tel'],
    input[inputmode='url'],
    input[inputmode='numeric'],
    input[inputmode='decimal'] {
      direction: ltr;
      text-align: left;
    }

    /* Same idea for such a value rendered as text rather than typed into a field.
       Alignment is deliberately left alone here — these sit in badges and headings
       that get it from their own layout. */
    .ltr-value {
      direction: ltr;
      unicode-bidi: isolate;
    }

    .scrollable {
      overflow: auto;

      &::-webkit-scrollbar {
        width: 0;
        height: 0;
        display: none;
      }
    }

    .pointer {
      @media (hover: hover) {
        &:hover:not(:disabled) {
          cursor: pointer;
        }
      }
    }

    .underlined {
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: none;
      text-underline-position: under;
    }
  `,
)
